import { reactive } from "vue";

// Session cache for the admin user list so navigating Users → Metrics → Users
// doesn't re-show a blank spinner. Within the TTL the cache is authoritative;
// past it the cached rows still render instantly while a background refetch
// runs. The cache is keyed to the signed-in uid: one account's directory data
// is never served to another account in the same browser session.
const USERS_CACHE_TTL_MS = 60000;

export const adminUsersStore = reactive({
  // Mapped user rows (including precomputed searchKey), or null when never fetched.
  users: null,
  fetchedAt: 0,
  forUid: null,
});

// Module-level so remounts share it: only one directory request per tab at a
// time, and an in-flight response can't overwrite a newer one.
let inFlightPromise = null;
let inFlightUid = null;
// Bumped on every cache clear: a request started before a sign-out must not
// repopulate the cache after a later sign-in — even for the same uid.
let cacheGeneration = 0;

export function usersCacheFor(uid) {
  if (!uid || adminUsersStore.forUid !== uid) return null;
  return adminUsersStore.users;
}

export function isUsersCacheFresh(uid, now = Date.now()) {
  return (
    usersCacheFor(uid) !== null &&
    now - adminUsersStore.fetchedAt < USERS_CACHE_TTL_MS
  );
}

// Called from the sign-out flow: don't keep one account's directory data
// parked in the heap after they leave, and drop any in-flight result.
export function clearAdminUsersCache() {
  adminUsersStore.users = null;
  adminUsersStore.fetchedAt = 0;
  adminUsersStore.forUid = null;
  inFlightUid = null;
  inFlightPromise = null;
  cacheGeneration += 1;
}

export function fetchUsersDeduped(uid, fetcher) {
  if (inFlightPromise && inFlightUid === uid) {
    return inFlightPromise;
  }
  inFlightUid = uid;
  const generation = cacheGeneration;
  const request = fetcher()
    .then((users) => {
      // Drop the result if the signed-in user changed OR the cache was
      // cleared (sign-out) while it was in flight — a same-uid re-login must
      // not receive data fetched under the previous session.
      if (inFlightUid === uid && generation === cacheGeneration) {
        adminUsersStore.users = users;
        adminUsersStore.fetchedAt = Date.now();
        adminUsersStore.forUid = uid;
      }
      return users;
    })
    .finally(() => {
      if (inFlightPromise === request) {
        inFlightPromise = null;
      }
    });
  inFlightPromise = request;
  return request;
}
